import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Descriptions, Input, Modal, Select, Space, Table, Tag, Typography, message,
} from 'antd';
import {
  CopyOutlined, EyeOutlined, LinkOutlined, ReloadOutlined, SyncOutlined, WarningOutlined,
} from '@ant-design/icons';
import api from '../services/api';

const { Text } = Typography;

const STATUS_META = {
  pending: { color: 'processing', text: '等待转储' },
  uploading: { color: 'processing', text: '上传中' },
  ready: { color: 'success', text: '已转储' },
  failed: { color: 'error', text: '转储失败' },
  expired: { color: 'default', text: '已过期' },
};

const formatBytes = (n) => {
  if (!n || n <= 0) return '-';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const copy = async (text) => {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    message.success('已复制');
  } catch {
    message.error('复制失败，请手动选择');
  }
};

const W3TransferLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [retryTarget, setRetryTarget] = useState(null);
  const [retryConfirm, setRetryConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const pollTimer = useRef(null);

  const load = useCallback(async (nextPage = page, nextSize = pageSize) => {
    setLoading(true);
    try {
      const params = { page: nextPage, page_size: nextSize };
      if (status) params.status = status;
      if (q.trim()) params.q = q.trim();
      const res = await api.get('/admin/w3-transfers', { params });
      const data = res?.data || {};
      setLogs(data.list || []);
      setTotal(data.total || 0);
      setPage(data.page || nextPage);
      setPageSize(data.page_size || nextSize);
    } catch (e) {
      message.error(e?.response?.data?.message || '加载转储日志失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, status, q]);

  useEffect(() => {
    load(1, pageSize);
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = () => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    let ticks = 0;
    pollTimer.current = setInterval(() => {
      ticks += 1;
      load(page, pageSize);
      if (ticks >= 24) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    }, 5000);
  };

  const openDetail = async (row) => {
    setDetailLoading(true);
    setDetail({ ...row, signed_url: '' });
    try {
      const res = await api.get(`/admin/w3-transfers/${row.id}`);
      setDetail(res?.data || row);
    } catch (e) {
      message.error(e?.response?.data?.message || '加载详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const resign = async (row) => {
    setActionLoading(true);
    try {
      const res = await api.post(`/admin/w3-transfers/${row.id}/resign`);
      const url = res?.data?.signed_url;
      if (url) {
        setDetail(prev => ({ ...(prev || row), signed_url: url }));
        message.success('已生成新的签名链接');
        await copy(url);
      } else {
        message.error(res?.message || '生成链接失败');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || '生成链接失败');
    } finally {
      setActionLoading(false);
    }
  };

  const doRetransfer = async () => {
    if (!retryTarget || !retryConfirm) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/admin/w3-transfers/${retryTarget.id}/retransfer`, { confirm: true });
      if (res?.code === 0) {
        message.success(`已开始二次转储，预扣 $${Number(res.data?.charge_amount || 0).toFixed(4)}`);
        setRetryTarget(null);
        setRetryConfirm(false);
        startPolling();
      } else {
        message.error(res?.message || '二次转储失败');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || '二次转储失败');
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      width: 155,
      render: v => <Text style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: '任务 / 用户',
      key: 'task',
      width: 210,
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontFamily: 'monospace' }}>{r.task_id}</div>
          <Text type="secondary">用户 #{r.user_id}</Text>
        </div>
      ),
    },
    {
      title: '模型',
      key: 'model',
      width: 150,
      render: (_, r) => <span style={{ fontSize: 12 }}>{r.model}<br /><Text type="secondary">{r.resolution || '-'}</Text></span>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v, r) => (
        <Space direction="vertical" size={2}>
          <Tag color={STATUS_META[v]?.color || 'default'}>{STATUS_META[v]?.text || v}</Tag>
          <Text type="secondary" style={{ fontSize: 11 }}>尝试 {r.attempt || 0} 次</Text>
        </Space>
      ),
    },
    {
      title: '大小 / 耗时',
      key: 'size',
      width: 120,
      render: (_, r) => <span style={{ fontSize: 12 }}>{formatBytes(r.file_size)}<br /><Text type="secondary">{r.duration_ms ? `${(r.duration_ms / 1000).toFixed(1)}s` : '-'}</Text></span>,
    },
    {
      title: '扣费 / 退款',
      key: 'money',
      width: 140,
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {r.charge_amount > 0 ? `扣 $${Number(r.charge_amount).toFixed(4)}` : '—'}
          <br />
          <Text type="secondary">{r.refund_status === 'refunded' ? `退 $${Number(r.refunded_amount || 0).toFixed(4)}` : (r.refund_status || '')}</Text>
        </span>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 210,
      render: (_, r) => {
        const failedRow = r.status === 'failed' || r.status === 'expired';
        const current = r.record_storage_status;
        const blocked = failedRow && current && current !== 'failed' && current !== 'expired';
        return (
          <Space size={4} wrap>
            <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r)}>详情</Button>
            {r.status === 'ready' && (
              <Button size="small" icon={<LinkOutlined />} loading={actionLoading} onClick={() => resign(r)}>重签</Button>
            )}
            {failedRow && !blocked && (
              <Button size="small" danger icon={<SyncOutlined />} onClick={() => { setRetryTarget(r); setRetryConfirm(false); }}>
                二次转储
              </Button>
            )}
            {blocked && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {current === 'ready' ? '已由后续转储完成，请到成功日志重签' : '转储进行中，请稍后刷新'}
              </Text>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Alert
        type="warning"
        showIcon
        icon={<WarningOutlined />}
        style={{ marginBottom: 12 }}
        message="转储日志仅管理员可见：上游原始链接、扣费与退款记录都在这里。二次转储会再次扣用户费用，失败会自动退回。"
      />
      <Space style={{ marginBottom: 12 }} wrap>
        <Input.Search
          allowClear
          placeholder="任务ID / 用户ID / 用户签名链接 / 上游链接"
          style={{ width: 420 }}
          value={q}
          onChange={e => setQ(e.target.value)}
          onSearch={() => load(1, pageSize)}
        />
        <Select
          allowClear
          placeholder="状态"
          style={{ width: 130 }}
          value={status || undefined}
          onChange={v => setStatus(v || '')}
          options={Object.entries(STATUS_META).map(([value, m]) => ({ value, label: m.text }))}
        />
        <Button icon={<ReloadOutlined />} onClick={() => load(page, pageSize)} loading={loading}>刷新</Button>
      </Space>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={logs}
        columns={columns}
        scroll={{ x: 980 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (p, s) => load(p, s),
        }}
      />

      <Modal
        open={!!detail}
        title="转储详情"
        footer={null}
        width={760}
        onCancel={() => setDetail(null)}
      >
        {detail && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="任务 ID"><Text copyable style={{ fontFamily: 'monospace' }}>{detail.task_id}</Text></Descriptions.Item>
            <Descriptions.Item label="用户 / 模型">#{detail.user_id} · {detail.model} {detail.resolution || ''}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={STATUS_META[detail.status]?.color || 'default'}>{STATUS_META[detail.status]?.text || detail.status}</Tag>
              {detail.refund_status && <Text type="secondary">退款：{detail.refund_status}</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="上游原始链接（仅后台）">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text copyable={{ text: detail.upstream_url }} style={{ wordBreak: 'break-all', fontSize: 12 }}>{detail.upstream_url || '-'}</Text>
                <Button size="small" icon={<CopyOutlined />} onClick={() => copy(detail.upstream_url)}>复制上游链接</Button>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="OSS storage_key">
              <Text copyable style={{ wordBreak: 'break-all', fontSize: 12 }}>{detail.storage_key || '-'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="当前签名链接">
              {detail.signed_url ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text style={{ wordBreak: 'break-all', fontSize: 12 }}>{detail.signed_url}</Text>
                  <Button size="small" icon={<CopyOutlined />} onClick={() => copy(detail.signed_url)}>复制签名链接</Button>
                </Space>
              ) : <Text type="secondary">暂无（文件已过期或未上传完成）</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="文件大小 / 耗时">
              {formatBytes(detail.file_size)} / {detail.duration_ms ? `${(detail.duration_ms / 1000).toFixed(1)}s` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="扣费 / 退款">
              {detail.charge_amount > 0 ? `扣 $${Number(detail.charge_amount).toFixed(4)}` : '未扣费'}
              {detail.refunded_amount > 0 ? ` / 退 $${Number(detail.refunded_amount).toFixed(4)}` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="时间">
              创建：{detail.created_at || '-'}<br />
              开始：{detail.started_at || '-'}<br />
              完成：{detail.finished_at || '-'}<br />
              清理：{detail.cleaned_at || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="错误">
              <Text type="danger" style={{ fontSize: 12 }}>{detail.error_msg || '-'}</Text>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        open={!!retryTarget}
        title="二次转储确认"
        onOk={doRetransfer}
        onCancel={() => { setRetryTarget(null); setRetryConfirm(false); }}
        confirmLoading={actionLoading}
        okText="确认二次转储"
        okButtonProps={{ danger: true, disabled: !retryConfirm }}
      >
        {retryTarget && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="二次转储会再次扣费"
              description={`任务 ${retryTarget.task_id} 将重新下载上游视频并上传 OSS。成功后系统会再次从用户账户扣除 $${Number(retryTarget.charge_amount || 0).toFixed(4)}；失败会自动退回本次扣费。`}
            />
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={retryConfirm}
                onChange={e => setRetryConfirm(e.target.checked)}
                style={{ marginTop: 4 }}
              />
              <span>我已确认已告知用户，二次转储成功后将再次扣费；用户端会收到扣费提示。</span>
            </label>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default W3TransferLogs;
